import { InfoList } from '../InfoList';
import {
  DELIVERY_CHANNEL,
  formatScheduleDate,
  formatScheduleTime,
  isScheduledSend,
  resolveDeliveryChannels,
} from '@shared/lib/deliverySummary';

/**
 * The timing row plus one row per channel, for the "sent" screens of
 * the assessment and resource panels.
 *
 * Both screens used to state the channel in prose and always named
 * email, so an SMS-only send read as an email one, and the address
 * line ended on a dangling "to" when the email field was empty. A row
 * per channel is what makes both impossible: no channel, no row.
 *
 * `extraItems` are the per-flow lines (secure link, auto-sync) that
 * follow the delivery ones.
 */

const formatLocalTime = date =>
  (date ?? new Date()).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

/**
 * Null rather than a half-sentence when the schedule cannot be stated:
 * both panels refuse to submit a scheduled send without a date, so
 * this is unreachable, and a row reading "Scheduled for ," would be
 * worse than no row at all.
 */
const buildScheduledItem = (values, done) => {
  const date = formatScheduleDate(values?.scheduledDate);
  if (!date) return null;

  const time = formatScheduleTime(values?.scheduleDeliverTime);
  const body = time
    ? done.scheduledFor.replace('{{date}}', date).replace('{{time}}', time)
    : done.scheduledForDate.replace('{{date}}', date);

  return { strong: done.scheduledSend, body };
};

const DeliverySummaryList = ({ values, done, extraItems = [], now }) => {
  const scheduled = isScheduledSend(values?.sendingOption);

  const timingItem = scheduled
    ? buildScheduledItem(values, done)
    : {
        strong: done.sentNow,
        body: done.sentNowAt.replace('{{time}}', formatLocalTime(now)),
      };

  const channelItems = resolveDeliveryChannels(values).map(({ channel, address }) => ({
    strong: channel === DELIVERY_CHANNEL.email ? done.channelEmail : done.channelSms,
    body: done.channelTo.replace('{{address}}', address),
  }));

  return <InfoList items={[timingItem, ...channelItems, ...extraItems].filter(Boolean)} />;
};

export default DeliverySummaryList;
